// src/modules/hr/pages/HrDashboard.jsx — Phase 24
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, Alert, Button, Tooltip, Table, Tag, List } from 'antd';
import {
  TeamOutlined, UserAddOutlined, CalendarOutlined, LaptopOutlined,
  ReloadOutlined, RiseOutlined, LogoutOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchHrDashboard } from '../api';
import { HOLIDAY_TYPE_OPTIONS, tagColor, tagLabel } from '../constants/hrConstants';

const { Title, Text } = Typography;

export default function HrDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetchHrDashboard();
      setData(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load HR dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><TeamOutlined style={{ marginRight: 8 }} />HR Dashboard</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>People, attendance, assets & holidays at a glance</Text>
        </Col>
        <Col><Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} /></Tooltip></Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Row gutter={[12, 12]}>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="Total Employees" value={data?.totalEmployees || 0} prefix={<TeamOutlined />} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="Active" value={data?.activeEmployees || 0} valueStyle={{ color: '#389e0d' }} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="Inactive / Exited" value={data?.inactiveEmployees || 0} valueStyle={{ color: '#8c8c8c' }} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="New Joiners (30d)" value={data?.newJoiners || 0} prefix={<UserAddOutlined />} valueStyle={{ color: '#1677ff' }} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="On Leave Today" value={data?.onLeaveToday || 0} prefix={<LogoutOutlined />} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="Assets Assigned" value={data?.assetsAssigned || 0} prefix={<LaptopOutlined />} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="Assets Pending Return" value={data?.assetsPendingReturn || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        <Col xs={12} sm={8} md={6}><Card size="small"><Statistic title="Attendance % (today)" value={data?.attendancePercentage || 0} suffix="%" prefix={<RiseOutlined />} valueStyle={{ color: '#389e0d' }} /></Card></Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        <Col xs={24} md={14}>
          <Card size="small" title="Branch-wise Employee Count">
            <Table
              size="small" rowKey={(r) => r.branchId || r.branchName || Math.random()}
              dataSource={data?.branchWiseEmployeeCount || []} pagination={false}
              columns={[
                { title: 'Branch', dataIndex: 'branchName', render: (v, r) => v ? `${v} (${r.branchCode || ''})` : <Text type="secondary">Unassigned</Text> },
                { title: 'Employees', dataIndex: 'count', align: 'right', width: 120 },
              ]}
              locale={{ emptyText: 'No data' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={10}>
          <Card size="small" title={<span><CalendarOutlined /> Upcoming Holidays (30d)</span>}>
            <List
              size="small"
              dataSource={data?.upcomingHolidays || []}
              locale={{ emptyText: 'No upcoming holidays' }}
              renderItem={(h) => (
                <List.Item>
                  <List.Item.Meta
                    title={<span>{h.name} <Tag color={tagColor(HOLIDAY_TYPE_OPTIONS, h.type)}>{tagLabel(HOLIDAY_TYPE_OPTIONS, h.type)}</Tag></span>}
                    description={dayjs(h.date).format('DD MMM YYYY') + (h.reason ? ` · ${h.reason}` : '')}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
